'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import Button from '@/components/ui/Button';
import { useNewsletter } from '@/hooks/useNewsletter';
import useStrapiHome from '@/hooks/useStrapiHome';

interface NewsletterSectionMobileProps {
  className?: string;
  onSubmit?: (email: string) => void | Promise<void>;
}

const normalizeHex = (hex: string | null | undefined): string | undefined => {
  if (!hex || typeof hex !== 'string') return undefined;
  const trimmed = hex.trim().replace(/^#/, '');
  return trimmed ? `#${trimmed}` : undefined;
};

/**
 * Posiciona el formulario en una grilla de 4 columnas × 2 filas:
 *
 *  Q1 | Q2 | Q3 | Q4   ← fila superior
 *  Q5 | Q6 | Q7 | Q8   ← fila inferior
 */
const getNewsletterFormPosition = (posicionBoton: string): React.CSSProperties => {
  const match = posicionBoton.match(/\d+/);
  if (!match) return { bottom: '15%', left: '5%' };

  const q   = Math.max(1, Math.min(8, parseInt(match[0], 10)));
  const col = (q - 1) % 4 + 1; // 1 | 2 | 3 | 4
  const row = Math.ceil(q / 4); // 1 (superior) | 2 (inferior)

  const horizontal: React.CSSProperties =
    col === 1 ? { left: '5%' }   :
    col === 2 ? { left: '30%' }  :
    col === 3 ? { right: '30%' } :
                { right: '5%' };

  const vertical: React.CSSProperties =
    row === 1 ? { top: '15%' } : { bottom: '15%' };

  return { ...horizontal, ...vertical };
};

const NewsletterSectionMobile: React.FC<NewsletterSectionMobileProps> = ({
  className = '',
  onSubmit,
}) => {
  const [email, setEmail] = useState('');
  const { data: strapiData } = useStrapiHome();
  const { subscribeAsync, isSubscribing, resetSubscribe } = useNewsletter();

  const newsletter = strapiData?.newsletterMobile;

  if (newsletter && !newsletter.visible) {
    return null;
  }

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      try {
        await Promise.resolve(onSubmit(email));
        setEmail('');
      } catch {
        /* mantener email */
      }
      return;
    }
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      resetSubscribe();
      await subscribeAsync({ email: trimmed });
      setEmail('');
    } catch {
      /* mantener email; useNewsletter onError */
    }
  };

  const submitting = !onSubmit && isSubscribing;
  const objetoFondo    = newsletter?.objetoFondo;
  const objetoSuperior = newsletter?.objetoSuperior;
  const ajuste         = newsletter?.objetoFondoAjuste;
  const bgColor        = normalizeHex(newsletter?.colorFondoHex);
  const tituloColor    = normalizeHex(newsletter?.colorTextoTituloHex);
  const descColor      = normalizeHex(newsletter?.colorTextoDescripcionHex);
  const objectFit: 'cover' | 'contain' = ajuste === 'sangrado' ? 'cover' : 'contain';

  const posicionBoton   = newsletter?.posicionBoton;
  const useQuadrantForm = typeof posicionBoton === 'string' && /\d/.test(posicionBoton);

  const renderForm = () => (
    <form data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:94:4" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="94" data-component-file="NewsletterSectionMobile.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-2%22%7D" onSubmit={handleNewsletterSubmit} className="space-y-2">
      <input data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:95:6" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="95" data-component-file="NewsletterSectionMobile.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22value%22%3A%22%5Bvar%3Aemail%5D%22%2C%22className%22%3A%22w-full%20px-3%20py-2%20rounded-lg%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-%5B%233d1c1a%5D%20font-tt-norms-regular%20text-sm%22%7D"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Tu email"
        disabled={submitting}
        className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d1c1a] font-tt-norms-regular text-sm"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.17)', color: '#3D1C1A' }}
        required
      />
      <style data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:105:6" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="105" data-component-file="NewsletterSectionMobile.tsx" data-component-name="style" data-component-content="%7B%22elementName%22%3A%22style%22%7D" jsx>{`
        input::placeholder { color: #3d1c1a !important; opacity: 1; }
      `}</style>
      {newsletter?.boton !== false && (
        <Button data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:109:8" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="109" data-component-file="NewsletterSectionMobile.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22min-w-%5B6.5rem%5D%20w-fit%20px-4%20py-2%20text-sm%20font-tt-norms-bold%20bg-%5B%233D1C1A%5D%20rounded-lg%20whitespace-nowrap%22%7D"
          type="submit"
          variant="primary"
          disabled={submitting}
          className="min-w-[6.5rem] w-fit px-4 py-2 text-sm font-tt-norms-bold bg-[#3D1C1A] rounded-lg whitespace-nowrap"
          style={{ color: '#FFFFFF' }}
        >
          {submitting ? 'Enviando...' : newsletter?.textoBoton || 'Unirme'}
        </Button>
      )}
    </form>
  );

  return (
    <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:123:4" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="123" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`py-0.5 ${className}`}>
      <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:124:6" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="124" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`rounded-[12px] p-4 relative overflow-hidden aspect-square ${!bgColor ? 'bg-global-6' : ''}`}
        style={bgColor ? { backgroundColor: bgColor } : undefined}
      >
        {/* Imagen de fondo (Objeto_fondo) */}
        {objetoFondo?.url && (
          <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:130:10" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="130" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20z-0%22%7D" className="absolute inset-0 z-0">
            <Image data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:131:12" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="131" data-component-file="NewsletterSectionMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22pointer-events-none%22%7D"
              src={objetoFondo.url}
              alt={objetoFondo.alternativeText ?? 'Newsletter fondo'}
              fill
              className="pointer-events-none"
              style={{ objectFit, objectPosition: 'center' }}
            />
          </div>
        )}

        {/* Imagen decorativa (Objeto_superior) — esquina inferior derecha */}
        <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:142:8" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="142" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20z-0%20flex%20items-end%20justify-end%22%7D" className="absolute inset-0 z-0 flex items-end justify-end">
          {objetoSuperior?.url ? (
            <Image data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:144:12" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="144" data-component-file="NewsletterSectionMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22pointer-events-none%20w-%5B280px%5D%20h-auto%20object-contain%22%7D"
              src={objetoSuperior.url}
              alt={objetoSuperior.alternativeText ?? 'Newsletter'}
              width={391}
              height={257}
              className="pointer-events-none w-[280px] h-auto object-contain"
            />
          ) : (
            <Image data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:152:12" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="152" data-component-file="NewsletterSectionMobile.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_imagennewsletter_1.png%22%2C%22alt%22%3A%22Newsletter%22%2C%22className%22%3A%22pointer-events-none%20w-%5B280px%5D%20h-auto%20object-contain%22%7D"
              src="/images/img_imagennewsletter_1.png"
              alt="Newsletter"
              width={391}
              height={257}
              className="pointer-events-none w-[280px] h-auto object-contain"
            />
          )}
        </div>

        {/*
          Layout base siempre presente (mantiene la estructura del aspect-square).
          En modo cuadrante, el formulario sale del flujo (absolute) y no aparece aquí.
        */}
        <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:166:8" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="166" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20flex%20flex-col%20justify-between%20h-%5B80%25%5D%20max-w-%5B70%25%5D%22%7D" className="relative z-10 flex flex-col justify-between h-[80%] max-w-[70%]">
          <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:167:10" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="167" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20gap-2%22%7D" className="flex flex-col gap-2">
            {newsletter?.titulo && (
              <p data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:169:14" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="169" data-component-file="NewsletterSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-extra-black%20text-%5B30px%5D%20leading-%5B34px%5D%20mb-1%22%7D"
                className="font-tt-norms-extra-black text-[30px] leading-[34px] mb-1"
                style={tituloColor ? { color: tituloColor } : { color: 'var(--color-global-1)' }}
              >
                {newsletter.titulo}
              </p>
            )}
            {newsletter?.descripcion && (
              <p data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:177:14" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="177" data-component-file="NewsletterSectionMobile.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-tt-norms-medium%20text-%5B16px%5D%20leading-%5B20px%5D%20mb-2%22%7D"
                className="font-tt-norms-medium text-[16px] leading-[20px] mb-2"
                style={descColor ? { color: descColor } : { color: 'var(--color-global-1)' }}
              >
                {newsletter.descripcion}
              </p>
            )}
          </div>

          {/* Formulario en flujo normal cuando no hay cuadrante */}
          {!useQuadrantForm && renderForm()}
        </div>

        {/* Formulario absoluto cuando hay cuadrante (4 col × 2 filas) */}
        {useQuadrantForm && (
          <div data-component-id="src/components/sections/mobile/NewsletterSectionMobile.tsx:192:10" data-component-path="src/components/sections/mobile/NewsletterSectionMobile.tsx" data-component-line="192" data-component-file="NewsletterSectionMobile.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20z-20%20flex%20flex-col%20gap-2%20w-%5Bmin(55%25%2C220px)%5D%22%7D"
            className="absolute z-20 flex flex-col gap-2 w-[min(55%,220px)]"
            style={getNewsletterFormPosition(posicionBoton!)}
          >
            {renderForm()}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterSectionMobile;
